/**
 * Validation Utilities
 *
 * Reusable validation functions for form inputs.
 */

// Name validation: Only letters (A-Z, a-z), spaces, and hyphens
export const NAME_REGEX = /^[A-Za-zÀ-ÿ\s-]*$/;

// Phone validation: Only digits and + sign (for international format)
export const PHONE_REGEX = /^[0-9]*$/;

// Email validation
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validates a name field (only letters and spaces)
 */
export function validateName(value: string): { valid: boolean; error?: string } {
  if (!value.trim()) {
    return { valid: false, error: 'Dieses Feld ist erforderlich' };
  }
  if (!NAME_REGEX.test(value)) {
    return { valid: false, error: 'Nur Buchstaben, Leerzeichen und Bindestriche erlaubt' };
  }
  return { valid: true };
}

/**
 * Validates a phone number (only digits)
 */
export function validatePhone(value: string): { valid: boolean; error?: string } {
  if (!value.trim()) {
    return { valid: false, error: 'Telefonnummer ist erforderlich' };
  }
  if (!PHONE_REGEX.test(value)) {
    return { valid: false, error: 'Nur Ziffern erlaubt' };
  }
  if (value.length < 6) {
    return { valid: false, error: 'Telefonnummer zu kurz' };
  }
  if (value.length > 15) {
    return { valid: false, error: 'Telefonnummer zu lang' };
  }
  return { valid: true };
}

/**
 * Validates an email address
 */
export function validateEmail(value: string, required: boolean = true): { valid: boolean; error?: string } {
  if (!value.trim()) {
    if (required) {
      return { valid: false, error: 'E-Mail ist erforderlich' };
    }
    return { valid: true };
  }
  if (!EMAIL_REGEX.test(value)) {
    return { valid: false, error: 'Ungültige E-Mail-Adresse' };
  }
  return { valid: true };
}

/**
 * Filters input to only allow valid name characters
 */
export function filterNameInput(value: string): string {
  return value.replace(/[^A-Za-zÀ-ÿ\s-]/g, '');
}

/**
 * Filters input to only allow digits
 */
export function filterPhoneInput(value: string): string {
  return value.replace(/[^0-9]/g, '');
}

/**
 * Country codes for phone input
 */
export interface CountryCode {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
}

// Common/priority countries shown first (DACH region + common)
export const PRIORITY_COUNTRY_CODES: CountryCode[] = [
  { code: 'DE', name: 'Deutschland', dialCode: '+49', flag: '🇩🇪' },
  { code: 'AT', name: 'Österreich', dialCode: '+43', flag: '🇦🇹' },
  { code: 'CH', name: 'Schweiz', dialCode: '+41', flag: '🇨🇭' },
];

// All countries sorted alphabetically by German name
export const ALL_COUNTRY_CODES: CountryCode[] = [
  { code: 'AF', name: 'Afghanistan', dialCode: '+93', flag: '🇦🇫' },
  { code: 'EG', name: 'Ägypten', dialCode: '+20', flag: '🇪🇬' },
  { code: 'AL', name: 'Albanien', dialCode: '+355', flag: '🇦🇱' },
  { code: 'DZ', name: 'Algerien', dialCode: '+213', flag: '🇩🇿' },
  { code: 'AD', name: 'Andorra', dialCode: '+376', flag: '🇦🇩' },
  { code: 'AO', name: 'Angola', dialCode: '+244', flag: '🇦🇴' },
  { code: 'AG', name: 'Antigua und Barbuda', dialCode: '+1268', flag: '🇦🇬' },
  { code: 'GQ', name: 'Äquatorialguinea', dialCode: '+240', flag: '🇬🇶' },
  { code: 'AR', name: 'Argentinien', dialCode: '+54', flag: '🇦🇷' },
  { code: 'AM', name: 'Armenien', dialCode: '+374', flag: '🇦🇲' },
  { code: 'AZ', name: 'Aserbaidschan', dialCode: '+994', flag: '🇦🇿' },
  { code: 'ET', name: 'Äthiopien', dialCode: '+251', flag: '🇪🇹' },
  { code: 'AU', name: 'Australien', dialCode: '+61', flag: '🇦🇺' },
  { code: 'BS', name: 'Bahamas', dialCode: '+1242', flag: '🇧🇸' },
  { code: 'BH', name: 'Bahrain', dialCode: '+973', flag: '🇧🇭' },
  { code: 'BD', name: 'Bangladesch', dialCode: '+880', flag: '🇧🇩' },
  { code: 'BB', name: 'Barbados', dialCode: '+1246', flag: '🇧🇧' },
  { code: 'BY', name: 'Belarus', dialCode: '+375', flag: '🇧🇾' },
  { code: 'BE', name: 'Belgien', dialCode: '+32', flag: '🇧🇪' },
  { code: 'BZ', name: 'Belize', dialCode: '+501', flag: '🇧🇿' },
  { code: 'BJ', name: 'Benin', dialCode: '+229', flag: '🇧🇯' },
  { code: 'BT', name: 'Bhutan', dialCode: '+975', flag: '🇧🇹' },
  { code: 'BO', name: 'Bolivien', dialCode: '+591', flag: '🇧🇴' },
  { code: 'BA', name: 'Bosnien und Herzegowina', dialCode: '+387', flag: '🇧🇦' },
  { code: 'BW', name: 'Botswana', dialCode: '+267', flag: '🇧🇼' },
  { code: 'BR', name: 'Brasilien', dialCode: '+55', flag: '🇧🇷' },
  { code: 'BN', name: 'Brunei', dialCode: '+673', flag: '🇧🇳' },
  { code: 'BG', name: 'Bulgarien', dialCode: '+359', flag: '🇧🇬' },
  { code: 'BF', name: 'Burkina Faso', dialCode: '+226', flag: '🇧🇫' },
  { code: 'BI', name: 'Burundi', dialCode: '+257', flag: '🇧🇮' },
  { code: 'CL', name: 'Chile', dialCode: '+56', flag: '🇨🇱' },
  { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳' },
  { code: 'CR', name: 'Costa Rica', dialCode: '+506', flag: '🇨🇷' },
  { code: 'DK', name: 'Dänemark', dialCode: '+45', flag: '🇩🇰' },
  { code: 'DE', name: 'Deutschland', dialCode: '+49', flag: '🇩🇪' },
  { code: 'DM', name: 'Dominica', dialCode: '+1767', flag: '🇩🇲' },
  { code: 'DO', name: 'Dominikanische Republik', dialCode: '+1809', flag: '🇩🇴' },
  { code: 'DJ', name: 'Dschibuti', dialCode: '+253', flag: '🇩🇯' },
  { code: 'EC', name: 'Ecuador', dialCode: '+593', flag: '🇪🇨' },
  { code: 'SV', name: 'El Salvador', dialCode: '+503', flag: '🇸🇻' },
  { code: 'CI', name: 'Elfenbeinküste', dialCode: '+225', flag: '🇨🇮' },
  { code: 'ER', name: 'Eritrea', dialCode: '+291', flag: '🇪🇷' },
  { code: 'EE', name: 'Estland', dialCode: '+372', flag: '🇪🇪' },
  { code: 'SZ', name: 'Eswatini', dialCode: '+268', flag: '🇸🇿' },
  { code: 'FJ', name: 'Fidschi', dialCode: '+679', flag: '🇫🇯' },
  { code: 'FI', name: 'Finnland', dialCode: '+358', flag: '🇫🇮' },
  { code: 'FR', name: 'Frankreich', dialCode: '+33', flag: '🇫🇷' },
  { code: 'GA', name: 'Gabun', dialCode: '+241', flag: '🇬🇦' },
  { code: 'GM', name: 'Gambia', dialCode: '+220', flag: '🇬🇲' },
  { code: 'GE', name: 'Georgien', dialCode: '+995', flag: '🇬🇪' },
  { code: 'GH', name: 'Ghana', dialCode: '+233', flag: '🇬🇭' },
  { code: 'GD', name: 'Grenada', dialCode: '+1473', flag: '🇬🇩' },
  { code: 'GR', name: 'Griechenland', dialCode: '+30', flag: '🇬🇷' },
  { code: 'GT', name: 'Guatemala', dialCode: '+502', flag: '🇬🇹' },
  { code: 'GN', name: 'Guinea', dialCode: '+224', flag: '🇬🇳' },
  { code: 'GW', name: 'Guinea-Bissau', dialCode: '+245', flag: '🇬🇼' },
  { code: 'GY', name: 'Guyana', dialCode: '+592', flag: '🇬🇾' },
  { code: 'HT', name: 'Haiti', dialCode: '+509', flag: '🇭🇹' },
  { code: 'HN', name: 'Honduras', dialCode: '+504', flag: '🇭🇳' },
  { code: 'IN', name: 'Indien', dialCode: '+91', flag: '🇮🇳' },
  { code: 'ID', name: 'Indonesien', dialCode: '+62', flag: '🇮🇩' },
  { code: 'IQ', name: 'Irak', dialCode: '+964', flag: '🇮🇶' },
  { code: 'IR', name: 'Iran', dialCode: '+98', flag: '🇮🇷' },
  { code: 'IE', name: 'Irland', dialCode: '+353', flag: '🇮🇪' },
  { code: 'IS', name: 'Island', dialCode: '+354', flag: '🇮🇸' },
  { code: 'IL', name: 'Israel', dialCode: '+972', flag: '🇮🇱' },
  { code: 'IT', name: 'Italien', dialCode: '+39', flag: '🇮🇹' },
  { code: 'JM', name: 'Jamaika', dialCode: '+1876', flag: '🇯🇲' },
  { code: 'JP', name: 'Japan', dialCode: '+81', flag: '🇯🇵' },
  { code: 'YE', name: 'Jemen', dialCode: '+967', flag: '🇾🇪' },
  { code: 'JO', name: 'Jordanien', dialCode: '+962', flag: '🇯🇴' },
  { code: 'KH', name: 'Kambodscha', dialCode: '+855', flag: '🇰🇭' },
  { code: 'CM', name: 'Kamerun', dialCode: '+237', flag: '🇨🇲' },
  { code: 'CA', name: 'Kanada', dialCode: '+1', flag: '🇨🇦' },
  { code: 'CV', name: 'Kap Verde', dialCode: '+238', flag: '🇨🇻' },
  { code: 'KZ', name: 'Kasachstan', dialCode: '+7', flag: '🇰🇿' },
  { code: 'QA', name: 'Katar', dialCode: '+974', flag: '🇶🇦' },
  { code: 'KE', name: 'Kenia', dialCode: '+254', flag: '🇰🇪' },
  { code: 'KG', name: 'Kirgisistan', dialCode: '+996', flag: '🇰🇬' },
  { code: 'KI', name: 'Kiribati', dialCode: '+686', flag: '🇰🇮' },
  { code: 'CO', name: 'Kolumbien', dialCode: '+57', flag: '🇨🇴' },
  { code: 'KM', name: 'Komoren', dialCode: '+269', flag: '🇰🇲' },
  { code: 'CD', name: 'Kongo (Demokratische Republik)', dialCode: '+243', flag: '🇨🇩' },
  { code: 'CG', name: 'Kongo (Republik)', dialCode: '+242', flag: '🇨🇬' },
  { code: 'HR', name: 'Kroatien', dialCode: '+385', flag: '🇭🇷' },
  { code: 'CU', name: 'Kuba', dialCode: '+53', flag: '🇨🇺' },
  { code: 'KW', name: 'Kuwait', dialCode: '+965', flag: '🇰🇼' },
  { code: 'LA', name: 'Laos', dialCode: '+856', flag: '🇱🇦' },
  { code: 'LS', name: 'Lesotho', dialCode: '+266', flag: '🇱🇸' },
  { code: 'LV', name: 'Lettland', dialCode: '+371', flag: '🇱🇻' },
  { code: 'LB', name: 'Libanon', dialCode: '+961', flag: '🇱🇧' },
  { code: 'LR', name: 'Liberia', dialCode: '+231', flag: '🇱🇷' },
  { code: 'LY', name: 'Libyen', dialCode: '+218', flag: '🇱🇾' },
  { code: 'LI', name: 'Liechtenstein', dialCode: '+423', flag: '🇱🇮' },
  { code: 'LT', name: 'Litauen', dialCode: '+370', flag: '🇱🇹' },
  { code: 'LU', name: 'Luxemburg', dialCode: '+352', flag: '🇱🇺' },
  { code: 'MG', name: 'Madagaskar', dialCode: '+261', flag: '🇲🇬' },
  { code: 'MW', name: 'Malawi', dialCode: '+265', flag: '🇲🇼' },
  { code: 'MY', name: 'Malaysia', dialCode: '+60', flag: '🇲🇾' },
  { code: 'MV', name: 'Malediven', dialCode: '+960', flag: '🇲🇻' },
  { code: 'ML', name: 'Mali', dialCode: '+223', flag: '🇲🇱' },
  { code: 'MT', name: 'Malta', dialCode: '+356', flag: '🇲🇹' },
  { code: 'MA', name: 'Marokko', dialCode: '+212', flag: '🇲🇦' },
  { code: 'MH', name: 'Marshallinseln', dialCode: '+692', flag: '🇲🇭' },
  { code: 'MR', name: 'Mauretanien', dialCode: '+222', flag: '🇲🇷' },
  { code: 'MU', name: 'Mauritius', dialCode: '+230', flag: '🇲🇺' },
  { code: 'MX', name: 'Mexiko', dialCode: '+52', flag: '🇲🇽' },
  { code: 'FM', name: 'Mikronesien', dialCode: '+691', flag: '🇫🇲' },
  { code: 'MD', name: 'Moldau', dialCode: '+373', flag: '🇲🇩' },
  { code: 'MC', name: 'Monaco', dialCode: '+377', flag: '🇲🇨' },
  { code: 'MN', name: 'Mongolei', dialCode: '+976', flag: '🇲🇳' },
  { code: 'ME', name: 'Montenegro', dialCode: '+382', flag: '🇲🇪' },
  { code: 'MZ', name: 'Mosambik', dialCode: '+258', flag: '🇲🇿' },
  { code: 'MM', name: 'Myanmar', dialCode: '+95', flag: '🇲🇲' },
  { code: 'NA', name: 'Namibia', dialCode: '+264', flag: '🇳🇦' },
  { code: 'NR', name: 'Nauru', dialCode: '+674', flag: '🇳🇷' },
  { code: 'NP', name: 'Nepal', dialCode: '+977', flag: '🇳🇵' },
  { code: 'NZ', name: 'Neuseeland', dialCode: '+64', flag: '🇳🇿' },
  { code: 'NI', name: 'Nicaragua', dialCode: '+505', flag: '🇳🇮' },
  { code: 'NL', name: 'Niederlande', dialCode: '+31', flag: '🇳🇱' },
  { code: 'NE', name: 'Niger', dialCode: '+227', flag: '🇳🇪' },
  { code: 'NG', name: 'Nigeria', dialCode: '+234', flag: '🇳🇬' },
  { code: 'KP', name: 'Nordkorea', dialCode: '+850', flag: '🇰🇵' },
  { code: 'MK', name: 'Nordmazedonien', dialCode: '+389', flag: '🇲🇰' },
  { code: 'NO', name: 'Norwegen', dialCode: '+47', flag: '🇳🇴' },
  { code: 'OM', name: 'Oman', dialCode: '+968', flag: '🇴🇲' },
  { code: 'AT', name: 'Österreich', dialCode: '+43', flag: '🇦🇹' },
  { code: 'PK', name: 'Pakistan', dialCode: '+92', flag: '🇵🇰' },
  { code: 'PW', name: 'Palau', dialCode: '+680', flag: '🇵🇼' },
  { code: 'PS', name: 'Palästina', dialCode: '+970', flag: '🇵🇸' },
  { code: 'PA', name: 'Panama', dialCode: '+507', flag: '🇵🇦' },
  { code: 'PG', name: 'Papua-Neuguinea', dialCode: '+675', flag: '🇵🇬' },
  { code: 'PY', name: 'Paraguay', dialCode: '+595', flag: '🇵🇾' },
  { code: 'PE', name: 'Peru', dialCode: '+51', flag: '🇵🇪' },
  { code: 'PH', name: 'Philippinen', dialCode: '+63', flag: '🇵🇭' },
  { code: 'PL', name: 'Polen', dialCode: '+48', flag: '🇵🇱' },
  { code: 'PT', name: 'Portugal', dialCode: '+351', flag: '🇵🇹' },
  { code: 'RW', name: 'Ruanda', dialCode: '+250', flag: '🇷🇼' },
  { code: 'RO', name: 'Rumänien', dialCode: '+40', flag: '🇷🇴' },
  { code: 'RU', name: 'Russland', dialCode: '+7', flag: '🇷🇺' },
  { code: 'SB', name: 'Salomonen', dialCode: '+677', flag: '🇸🇧' },
  { code: 'ZM', name: 'Sambia', dialCode: '+260', flag: '🇿🇲' },
  { code: 'WS', name: 'Samoa', dialCode: '+685', flag: '🇼🇸' },
  { code: 'SM', name: 'San Marino', dialCode: '+378', flag: '🇸🇲' },
  { code: 'ST', name: 'São Tomé und Príncipe', dialCode: '+239', flag: '🇸🇹' },
  { code: 'SA', name: 'Saudi-Arabien', dialCode: '+966', flag: '🇸🇦' },
  { code: 'SE', name: 'Schweden', dialCode: '+46', flag: '🇸🇪' },
  { code: 'CH', name: 'Schweiz', dialCode: '+41', flag: '🇨🇭' },
  { code: 'SN', name: 'Senegal', dialCode: '+221', flag: '🇸🇳' },
  { code: 'RS', name: 'Serbien', dialCode: '+381', flag: '🇷🇸' },
  { code: 'SC', name: 'Seychellen', dialCode: '+248', flag: '🇸🇨' },
  { code: 'SL', name: 'Sierra Leone', dialCode: '+232', flag: '🇸🇱' },
  { code: 'ZW', name: 'Simbabwe', dialCode: '+263', flag: '🇿🇼' },
  { code: 'SG', name: 'Singapur', dialCode: '+65', flag: '🇸🇬' },
  { code: 'SK', name: 'Slowakei', dialCode: '+421', flag: '🇸🇰' },
  { code: 'SI', name: 'Slowenien', dialCode: '+386', flag: '🇸🇮' },
  { code: 'SO', name: 'Somalia', dialCode: '+252', flag: '🇸🇴' },
  { code: 'ES', name: 'Spanien', dialCode: '+34', flag: '🇪🇸' },
  { code: 'LK', name: 'Sri Lanka', dialCode: '+94', flag: '🇱🇰' },
  { code: 'KN', name: 'St. Kitts und Nevis', dialCode: '+1869', flag: '🇰🇳' },
  { code: 'LC', name: 'St. Lucia', dialCode: '+1758', flag: '🇱🇨' },
  { code: 'VC', name: 'St. Vincent und die Grenadinen', dialCode: '+1784', flag: '🇻🇨' },
  { code: 'ZA', name: 'Südafrika', dialCode: '+27', flag: '🇿🇦' },
  { code: 'SD', name: 'Sudan', dialCode: '+249', flag: '🇸🇩' },
  { code: 'KR', name: 'Südkorea', dialCode: '+82', flag: '🇰🇷' },
  { code: 'SS', name: 'Südsudan', dialCode: '+211', flag: '🇸🇸' },
  { code: 'SR', name: 'Suriname', dialCode: '+597', flag: '🇸🇷' },
  { code: 'SY', name: 'Syrien', dialCode: '+963', flag: '🇸🇾' },
  { code: 'TJ', name: 'Tadschikistan', dialCode: '+992', flag: '🇹🇯' },
  { code: 'TW', name: 'Taiwan', dialCode: '+886', flag: '🇹🇼' },
  { code: 'TZ', name: 'Tansania', dialCode: '+255', flag: '🇹🇿' },
  { code: 'TH', name: 'Thailand', dialCode: '+66', flag: '🇹🇭' },
  { code: 'TL', name: 'Timor-Leste', dialCode: '+670', flag: '🇹🇱' },
  { code: 'TG', name: 'Togo', dialCode: '+228', flag: '🇹🇬' },
  { code: 'TO', name: 'Tonga', dialCode: '+676', flag: '🇹🇴' },
  { code: 'TT', name: 'Trinidad und Tobago', dialCode: '+1868', flag: '🇹🇹' },
  { code: 'TD', name: 'Tschad', dialCode: '+235', flag: '🇹🇩' },
  { code: 'CZ', name: 'Tschechien', dialCode: '+420', flag: '🇨🇿' },
  { code: 'TN', name: 'Tunesien', dialCode: '+216', flag: '🇹🇳' },
  { code: 'TR', name: 'Türkei', dialCode: '+90', flag: '🇹🇷' },
  { code: 'TM', name: 'Turkmenistan', dialCode: '+993', flag: '🇹🇲' },
  { code: 'TV', name: 'Tuvalu', dialCode: '+688', flag: '🇹🇻' },
  { code: 'UG', name: 'Uganda', dialCode: '+256', flag: '🇺🇬' },
  { code: 'UA', name: 'Ukraine', dialCode: '+380', flag: '🇺🇦' },
  { code: 'HU', name: 'Ungarn', dialCode: '+36', flag: '🇭🇺' },
  { code: 'UY', name: 'Uruguay', dialCode: '+598', flag: '🇺🇾' },
  { code: 'US', name: 'USA', dialCode: '+1', flag: '🇺🇸' },
  { code: 'UZ', name: 'Usbekistan', dialCode: '+998', flag: '🇺🇿' },
  { code: 'VU', name: 'Vanuatu', dialCode: '+678', flag: '🇻🇺' },
  { code: 'VA', name: 'Vatikanstadt', dialCode: '+379', flag: '🇻🇦' },
  { code: 'VE', name: 'Venezuela', dialCode: '+58', flag: '🇻🇪' },
  { code: 'AE', name: 'Vereinigte Arabische Emirate', dialCode: '+971', flag: '🇦🇪' },
  { code: 'GB', name: 'Vereinigtes Königreich', dialCode: '+44', flag: '🇬🇧' },
  { code: 'VN', name: 'Vietnam', dialCode: '+84', flag: '🇻🇳' },
  { code: 'CF', name: 'Zentralafrikanische Republik', dialCode: '+236', flag: '🇨🇫' },
  { code: 'CY', name: 'Zypern', dialCode: '+357', flag: '🇨🇾' },
];

// Combined list: priority countries first, then all others (excluding duplicates)
export const COUNTRY_CODES: CountryCode[] = [
  ...PRIORITY_COUNTRY_CODES,
  ...ALL_COUNTRY_CODES.filter(
    (c) => !PRIORITY_COUNTRY_CODES.some((p) => p.code === c.code)
  ),
];

/**
 * Get country by code
 */
export function getCountryByCode(code: string): CountryCode | undefined {
  return ALL_COUNTRY_CODES.find(c => c.code === code);
}

/**
 * Parse phone number to extract country code and local number
 * Sorts dial codes by length (longest first) to match correctly
 * e.g., +1242 (Bahamas) should match before +1 (USA/Canada)
 */
export function parsePhoneNumber(fullPhone: string): { countryCode: string; localNumber: string } {
  // Default to Germany
  let countryCode = 'DE';
  let localNumber = fullPhone;

  // Sort countries by dial code length (longest first) for correct matching
  const sortedCountries = [...ALL_COUNTRY_CODES].sort(
    (a, b) => b.dialCode.length - a.dialCode.length
  );

  // Try to match country dial code
  for (const country of sortedCountries) {
    if (fullPhone.startsWith(country.dialCode)) {
      countryCode = country.code;
      localNumber = fullPhone.slice(country.dialCode.length).replace(/\s/g, '');
      break;
    }
  }

  // If no country code found and starts with 0, assume Germany
  if (localNumber.startsWith('0')) {
    localNumber = localNumber.slice(1);
  }

  return { countryCode, localNumber: localNumber.replace(/[^0-9]/g, '') };
}

/**
 * Format phone number with country code
 */
export function formatPhoneWithCountry(countryCode: string, localNumber: string): string {
  const country = getCountryByCode(countryCode);
  if (!country) return localNumber;

  // Remove leading zero if present
  const cleaned = localNumber.startsWith('0') ? localNumber.slice(1) : localNumber;
  return `${country.dialCode}${cleaned}`;
}
